import {
  ChronoUnit,
  convert,
  DateTimeFormatter,
  DayOfWeek,
  Duration,
  Instant,
  LocalDateTime,
  nativeJs,
  ZonedDateTime,
  ZoneId,
} from "@js-joda/core";
import "@js-joda/timezone";
import { ColumnSortEvent } from "primereact/column";
import { T } from "services/lang/LanguageService";
import { errorToaster } from "components/pages/measurement-devices/utils/utils";
import {
  DATE_FORMATTER_DAYS,
  DATE_FORMATTER_HOURS,
  DATE_FORMATTER_MINUTES,
  DATE_FORMATTER_MONTHS,
  DATE_FORMATTER_SECONDS,
  DATE_FORMATTER_YEARS,
} from "./constants";

export const BROWSER_ZONE_ID = ZoneId.systemDefault();
export const LT_ZONE_ID = ZoneId.of("Europe/Vilnius");
export const UTC_ZONE_ID = ZoneId.of("UTC");

export type PossibleDateTypes = ZonedDateTime | Instant | Date | string | number;
export type PossibleDateTypesOrNull = PossibleDateTypes | null | undefined;
export type PossibleTimeUnitTypes = ChronoUnit | DweenTimeUnitEnum | DweenTimeUnit;

export function durationSeconds(duration: number, durationUnit: string): number {
  switch (durationUnit) {
    case "SECONDS":
      return duration;
    case "MINUTES":
      return duration * 60;
    case "HOURS":
      return duration * 60 * 60;
    case "DAYS":
      return duration * 60 * 60 * 24;
    case "WEEKS":
      return duration * 60 * 60 * 24 * 7;
    case "MONTHS":
      return duration * 60 * 60 * 24 * 7 * 30;
    default:
      throw new Error(T("error.not-known-time-unit"));
  }
}

export function formatDuration(secs: number) {
  let fmt = "";

  const days = Math.floor(secs / (3600 * 24));
  const hours = Math.floor((secs - days * 3600 * 24) / 3600);
  const minutes = Math.floor((secs - days * 3600 * 24 - hours * 3600) / 60);
  const seconds = secs - days * 3600 * 24 - hours * 3600 - minutes * 60;

  if (days > 0) {
    fmt += `${days}d `;
  }
  if (hours > 0) {
    fmt += `${hours}h `;
  }
  if (minutes > 0) {
    fmt += `${minutes}m `;
  }
  if (seconds >= 0) {
    fmt += `${seconds.toFixed(0)}s`;
  }

  return fmt;
}

export function getDiffSeconds(date1: Date | string, date2: Date | string): number {
  const d1 = new Date(toNativeDate(date1)!);
  const d2 = new Date(toNativeDate(date2)!);

  return (+d1 - +d2) / 1000;
}

export const toInstant = <T extends PossibleDateTypesOrNull>(value: T): WithSameNullability<T, Instant> => {
  if (!value) return null as WithSameNullability<T, Instant>;

  if (value instanceof Instant) return value;

  if (typeof value === "string") {
    try {
      return Instant.parse(value);
    } catch (error) {
      const parsed = new Date(value).toInstant();
      console.warn("Failed to parse string to Instant, defaulting to Date parsing", value);
      return parsed;
    }
  }

  if (value instanceof Date) {
    return value.toInstant();
  }

  if (value instanceof ZonedDateTime) {
    return value.toInstant();
  }

  if (typeof value === "number") {
    return Instant.ofEpochMilli(value);
  }

  throw new Error(`${T("error.invalid-instant-type")} ${value} (${typeof value})`);
};

export function getMonday(date: Date | string | Instant | ZonedDateTime, zoneId: ZoneId): Date {
  const zonedDate = toZonedDateTime(date, zoneId);

  const day = zonedDate.dayOfWeek().value();
  const diff = day === 0 ? -6 : 1 - day; // adjust when day is monday

  return convert(zonedDate.plusDays(diff)).toDate();
}

export const toChronoUnit = (unit: any): ChronoUnit => {
  if (typeof unit === "object") {
    const { _name: name } = unit;
    return ChronoUnit[name?.toUpperCase() as keyof typeof ChronoUnit] || null;
  }
  const resolvedUnit = ChronoUnit[unit?.toString().toUpperCase() as keyof typeof ChronoUnit];
  return resolvedUnit || null;
};

export const fmtDateUntilUnit = (date: PossibleDateTypes, formatUntil: DweenTimeUnitEnum | undefined, zoneId: ZoneId) => {
  const displayDate = toZonedDateTime(date, zoneId);
  const unit = formatUntil ? toChronoUnit(formatUntil) : ChronoUnit.SECONDS;

  switch (unit) {
    case ChronoUnit.SECONDS:
      return fmtDatetime(displayDate, zoneId, DATE_FORMATTER_SECONDS);
    case ChronoUnit.MINUTES:
      return fmtDatetime(displayDate, zoneId, DATE_FORMATTER_MINUTES);
    case ChronoUnit.HOURS:
      return fmtDatetime(displayDate, zoneId, DATE_FORMATTER_HOURS);
    case ChronoUnit.DAYS:
      return fmtDatetime(displayDate, zoneId, DATE_FORMATTER_DAYS);
    case ChronoUnit.MONTHS:
      return fmtDatetime(displayDate, zoneId, DATE_FORMATTER_MONTHS);
    case ChronoUnit.YEARS:
      return fmtDatetime(displayDate, zoneId, DATE_FORMATTER_YEARS);

    default:
      throw Error(`Not supported for ${formatUntil}`);
  }
};

export function fmtDatetimeAdvanced(
  date: PossibleDateTypes | undefined | null,
  zoneId: ZoneId,
  conf?: {
    defaultToNow?: boolean;
    companyZoneId?: ZoneId;
    pattern?: DateTimeFormatter | string;
    showOffset?: boolean;
  }
): string {
  const usedConf = conf || {};
  if (!date && !usedConf.defaultToNow) return "";

  if (usedConf?.pattern instanceof DateTimeFormatter) {
    usedConf.pattern = usedConf.pattern || DATE_FORMATTER_MINUTES;
  } else {
    usedConf.pattern = usedConf.pattern ? DateTimeFormatter.ofPattern(usedConf.pattern) : DATE_FORMATTER_MINUTES;
  }

  const zonedTime = toZonedDateTime(date || Instant.now(), zoneId);

  const localDateTime = zonedTime.toLocalDateTime();
  let dateStr = localDateTime.format(usedConf.pattern);
  if (usedConf.showOffset) {
    const offset = zonedTime.offset().toString();
    dateStr += ` [${offset}]`;
  }

  return dateStr;
}

export function fmtDatetime(
  date: PossibleDateTypes | undefined | null,
  displayZoneId: ZoneId,
  pattern: string | DateTimeFormatter | undefined = undefined,
  showOffset: boolean = false,
  companyZoneId?: ZoneId | undefined
) {
  return fmtDatetimeAdvanced(date, displayZoneId, {
    defaultToNow: false,
    companyZoneId,
    pattern,
    showOffset,
  });
}

export type DweenTimeUnit = keyof typeof DweenTimeUnitEnum;
export enum DweenTimeUnitEnum {
  MILLIS = "MILLIS",
  SECONDS = "SECONDS",
  MINUTES = "MINUTES",
  HALFHOUR = "HALFHOUR",
  HOURS = "HOURS",
  DAYS = "DAYS",
  WEEKS = "WEEKS",
  MONTHS = "MONTHS",
  YEARS = "YEARS",
}

export namespace JodaTimeUtils {
  const toDweenUnit = (unit: any): DweenTimeUnitEnum => {
    if (typeof unit === "object") {
      const { _name: name } = unit;
      return DweenTimeUnitEnum[name?.toUpperCase() as keyof typeof DweenTimeUnitEnum] || null;
    }
    const resolvedUnit = DweenTimeUnitEnum[unit?.toString().toUpperCase() as keyof typeof DweenTimeUnitEnum];
    return resolvedUnit || null;
  };

  const validDweenTimeUnit = (unit: any): unit is DweenTimeUnitEnum => {
    const dweenUnit = toDweenUnit(unit);
    return dweenUnit != null;
  };

  export const truncateToZoned = (
    date: PossibleDateTypes,
    unit: PossibleTimeUnitTypes | undefined,
    zoneId: ZoneId
  ): ZonedDateTime => {
    if (zoneId == null) {
      throw new Error("ZoneId must be provided.");
    }

    if (unit == null) {
      return toZonedDateTime(date, zoneId);
    }

    if (!validDweenTimeUnit(unit)) {
      throw new Error("Unhandled time unit.");
    }
    unit = toDweenUnit(unit);

    const tempDate = toZonedDateTime(date, zoneId);

    switch (unit) {
      case DweenTimeUnitEnum.MILLIS:
      case DweenTimeUnitEnum.SECONDS:
      case DweenTimeUnitEnum.MINUTES:
      case DweenTimeUnitEnum.HOURS:
      case DweenTimeUnitEnum.DAYS:
        return tempDate.truncatedTo(toChronoUnit(unit));
      case DweenTimeUnitEnum.WEEKS:
        return tempDate.truncatedTo(ChronoUnit.DAYS).with(DayOfWeek.MONDAY);
      case DweenTimeUnitEnum.MONTHS:
        return tempDate.truncatedTo(ChronoUnit.DAYS).withDayOfMonth(1);
      case DweenTimeUnitEnum.YEARS:
        return tempDate.truncatedTo(ChronoUnit.DAYS).withDayOfYear(1);

      case DweenTimeUnitEnum.HALFHOUR:
        return tempDate.truncatedTo(ChronoUnit.HOURS).plus(tempDate.minute() >= 30 ? 30 : 0, ChronoUnit.MINUTES);
      default:
        throw new Error("Unhandled time unit.");
    }
  };

  export const CeilingTo = (date: PossibleDateTypes, unit: ChronoUnit, zoneId: ZoneId): Date => {
    if (!validDweenTimeUnit(unit)) {
      throw new Error("Unhandled time unit.");
    }

    const zonedDate = toZonedDateTime(date, zoneId);
    const truncatedDate = truncateToZoned(date, unit, zoneId);

    let truncated: ZonedDateTime | null = null;
    switch (unit) {
      case ChronoUnit.MILLIS:
      case ChronoUnit.SECONDS:
      case ChronoUnit.MINUTES:
      case ChronoUnit.HOURS:
      case ChronoUnit.DAYS:
      case ChronoUnit.WEEKS:
      case ChronoUnit.MONTHS:
      case ChronoUnit.YEARS:
        if (truncatedDate.isBefore(zonedDate)) {
          truncated = truncatedDate.plus(1, unit);
        } else {
          truncated = truncatedDate;
        }
        break;
      default:
        throw new Error("Unhandled time unit.");
    }

    return toNativeDate(truncated);
  };

  export const truncateAndOffsetLocalDatetime = (
    date: LocalDateTime,
    truncateToUnit: DweenTimeUnit,
    offset: number,
    offsetUnit: DweenTimeUnit
  ): LocalDateTime => {
    const truncatedDate = date.truncatedTo(toChronoUnit(truncateToUnit));
    return truncatedDate.plus(offset, toChronoUnit(offsetUnit));
  };
}

export function truncateDate(date: PossibleDateTypes, truncateTo: PossibleTimeUnitTypes | undefined, zoneId: ZoneId): Date {
  return JodaTimeUtils.truncateToZoned(date, truncateTo, zoneId).toDate();
}

export function addToDate(
  date: Date | Instant | ZonedDateTime | string,
  amount: number,
  unit: DweenTimeUnitEnum | String | undefined
): Date {
  const d = toNativeDate(date);

  if (amount == null || unit == null) {
    return d;
  }

  switch (unit) {
    case DweenTimeUnitEnum.SECONDS:
      d.setSeconds(d.getSeconds() + amount);
      break;
    case DweenTimeUnitEnum.MINUTES:
      d.setMinutes(d.getMinutes() + amount);
      break;
    case DweenTimeUnitEnum.HALFHOUR:
      d.setMinutes(d.getMinutes() + amount * 30);
      break;
    case DweenTimeUnitEnum.HOURS:
      d.setHours(d.getHours() + amount);
      break;
    case DweenTimeUnitEnum.DAYS:
      d.setHours(d.getHours() + amount * 24);
      break;
    case DweenTimeUnitEnum.WEEKS:
      d.setHours(d.getHours() + amount * 24 * 7);
      break;
    case DweenTimeUnitEnum.MONTHS:
      d.setMonth(d.getMonth() + amount);
      break;
    case DweenTimeUnitEnum.YEARS:
      d.setFullYear(d.getFullYear() + amount);
      break;
    default:
      break;
  }
  return d;
}

export function forceToLtTzRemoveMe<T extends Date | undefined>(date: T, forceLt: boolean): T {
  if (!date) return undefined as T;
  if (!forceLt) return new Date(date) as T;

  return convert(nativeJs(date).withZoneSameLocal(LT_ZONE_ID)).toDate() as T;
}

export function primereactDataTableSortDateFunction(e: ColumnSortEvent) {
  if (e.data) {
    const orderMultiplier = e.order || 1;
    return [...e.data].sort((a, b) => {
      const valA = a.startsAt || a.start ? new Date(a.startsAt || a.start).getTime() : NaN;
      const valB = b.startsAt || b.start ? new Date(b.startsAt || b.start).getTime() : NaN;

      if (Number.isNaN(valA) && !Number.isNaN(valB)) {
        return -1 * orderMultiplier;
      }
      if (!Number.isNaN(valA) && Number.isNaN(valB)) {
        return orderMultiplier;
      }
      return (valA - valB) * orderMultiplier;
    });
  }

  return [];
}

function getUnitFromSeconds(seconds: number) {
  if (seconds < 60) {
    return ChronoUnit.SECONDS;
  }
  if (seconds < 3600) {
    return ChronoUnit.MINUTES;
  }
  if (seconds < 86400) {
    return ChronoUnit.HOURS;
  }
  return ChronoUnit.DAYS;
}

function getUnitLangKey(unit: ChronoUnit) {
  switch (unit) {
    case ChronoUnit.SECONDS:
      return "time.seconds";
    case ChronoUnit.MINUTES:
      return "time.minutes";
    case ChronoUnit.HOURS:
      return "time.hours";
    case ChronoUnit.DAYS:
      return "time.days";
    default:
      return "time.seconds";
  }
}

export function timeAgo(date: Date | string | undefined | null) {
  if (!date) return "";

  const secondsAgo = timeDifference(date);
  const unit = getUnitFromSeconds(secondsAgo);
  const unitLangKey = getUnitLangKey(unit);

  let count;
  switch (unit) {
    case ChronoUnit.SECONDS:
      count = secondsAgo;
      break;
    case ChronoUnit.MINUTES:
      count = Math.round(secondsAgo / 60);
      break;
    case ChronoUnit.HOURS:
      count = Math.round(secondsAgo / 3600);
      break;
    case ChronoUnit.DAYS:
      count = Math.round(secondsAgo / 86400);
      break;
    default:
      count = secondsAgo;
  }

  return T("message.time-ago", { count, unit: T(unitLangKey) });
}

export const timeDifference = (from: Date | string, to?: Date | string) => {
  const dateTo = to ? Instant.ofEpochMilli(new Date(from).getTime()) : Instant.now();
  const dateFrom = Instant.ofEpochMilli(new Date(from).getTime());
  const duration = Duration.between(dateFrom, dateTo).seconds();
  return Math.abs(duration);
};

export const dateBody = (date: string, dateFormat: DweenTimeUnitEnum, zoneId: ZoneId): string => {
  const dateDate = new Date(date);

  return fmtDateUntilUnit(dateDate, dateFormat, zoneId);
};

export const secondsToTimeSpan = (seconds: number) => {
  const timeSpan = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  };
  if (!seconds || typeof seconds !== "number" || seconds <= 0) return timeSpan;

  timeSpan.seconds = seconds;
  if (seconds < 60) return timeSpan;

  timeSpan.minutes = Math.floor(seconds / 60);
  timeSpan.seconds %= 60;
  if (timeSpan.minutes < 60) return timeSpan;

  timeSpan.hours = Math.floor(timeSpan.minutes / 60);
  timeSpan.minutes %= 60;
  if (timeSpan.hours < 24) return timeSpan;

  timeSpan.days = Math.floor(timeSpan.hours / 24);
  timeSpan.hours %= 24;
  return timeSpan;
};

export type TimeSpan = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

export const timeSpanToSeconds = (timeSpan: TimeSpan) => {
  if (!timeSpan || !(timeSpan instanceof Object) || Object.keys(timeSpan).length !== 4) return 0;

  const days = Math.abs(timeSpan.days) * 86400;
  const hours = Math.abs(timeSpan.hours) * 3600;
  const minutes = Math.abs(timeSpan.minutes) * 60;
  const seconds = Math.abs(timeSpan.seconds);
  return days + hours + minutes + seconds;
};

export const convertTimeSpanToString = (timeSpan: TimeSpan) => {
  if (!timeSpan) return "-";
  let timeString = "";

  if (timeSpan.days) {
    timeString += `${timeSpan.days}d `;
  }
  if (timeSpan.hours) {
    timeString += `${timeSpan.hours}h `;
  }
  if (timeSpan.minutes) {
    timeString += `${timeSpan.minutes}min `;
  }
  if (timeSpan.seconds) {
    timeString += `${timeSpan.seconds}s `;
  }
  return timeString.trim() || "-";
};

export const convertStringToTimeSpan = (timeSpanString: string) => {
  const timeSpan = {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  };

  if (!timeSpanString || timeSpanString === "-") return timeSpan;

  const timeSpans = timeSpanString.split(" ");

  timeSpans.forEach(ts => {
    if (ts.includes("d")) {
      timeSpan.days = parseInt(ts.replace("d", ""), 10);
    } else if (ts.includes("h")) {
      timeSpan.hours = parseInt(ts.replace("h", ""), 10);
    } else if (ts.includes("min")) {
      timeSpan.minutes = parseInt(ts.replace("min", ""), 10);
    } else if (ts.includes("s")) {
      timeSpan.seconds = parseInt(ts.replace("s", ""), 10);
    }
  });

  return timeSpan;
};

export const formatToIsoStringWithoutMillis = (date: Date): string => {
  return `${date.toISOString().split(".")[0]}Z`;
};

export const timeDuration = (start: string | undefined, end: string | undefined) => {
  if (!start || !end) return 0;
  return Duration.between(toInstant(start), toInstant(end)).seconds();
};

export const formatDateDuration = (start: string | undefined, end: string | undefined) => {
  if (!start || !end) return "-";
  let result;
  try {
    const offset = timeDuration(start, end);
    result = convertTimeSpanToString(secondsToTimeSpan(offset));
  } catch (error) {
    if (error instanceof Error) {
      result = error.message;
    }
    errorToaster(`Failed to parse "${start}" or "${end}" into date`);
  }
  return result;
};

export const getTimestamp = (val: unknown): number | null => {
  if (typeof val === "string" || typeof val === "number") {
    const date = new Date(val);
    return Number.isNaN(date.getTime()) ? null : date.getTime();
  }
  return null;
};

export const getRelativeOffsetForGroup = (groupStart: string, startTimeStr?: string, endTimeStr?: string): [number, number] => {
  if (!startTimeStr || !groupStart) return [0, 0];
  const groupStartTime = new Date(groupStart).getTime();
  const start = new Date(startTimeStr).getTime();
  const end = endTimeStr ? new Date(endTimeStr).getTime() : start;
  return [start - groupStartTime, end - groupStartTime];
};

// Dont use if not needed
// Coerces date to to display same local time as targetTz (timestamp changes!!!)
export const coerceDateToTimezone = (d: Date | undefined, targetTz: ZoneId) => {
  if (!d) return null;

  const date = toZonedDateTime(d, targetTz).withZoneSameLocal(BROWSER_ZONE_ID).toDate();

  return date;
};

// Dont use if not needed
// Coerces date back to browser local time (fixes timestamp from coerceDateToDisplay changes)
export const coerceDateToBrowser = (d: Date | undefined, targetTz: ZoneId) => {
  if (!d) return null;

  const date = toZonedDateTime(d, BROWSER_ZONE_ID).withZoneSameLocal(targetTz).toDate();

  return date;
};

export const isSameEpochTime = (date1: PossibleDateTypes | undefined, date2: PossibleDateTypes | undefined) => {
  const i1 = date1 ? toInstant(date1) : undefined;
  const i2 = date2 ? toInstant(date2) : undefined;
  return i1?.toEpochMilli() === i2?.toEpochMilli();
};

export const toLocalDateTime = <T extends string | undefined | null>(value: T): WithSameNullability<T, LocalDateTime> => {
  if (!value) return null as WithSameNullability<T, LocalDateTime>;

  try {
    return LocalDateTime.parse(value) as WithSameNullability<T, LocalDateTime>;
  } catch (error) {
    console.warn("Failed to parse string to LocalDateTime", value);
    return null as WithSameNullability<T, LocalDateTime>;
  }
};

export const toZonedDateTime = <T extends PossibleDateTypesOrNull | LocalDateTime>(
  value: T,
  zoneId: ZoneId
): WithSameNullability<T, ZonedDateTime> => {
  if (value instanceof LocalDateTime) {
    return value.atZone(zoneId);
  }

  return toInstant(value)?.atZone(zoneId) as WithSameNullability<T, ZonedDateTime>;
};

export const toNativeDateOrNow = (date: Instant | ZonedDateTime | Date | string | number | undefined): Date => {
  if (!date) return new Date();

  return toNativeDate(date);
};

export const toNativeDate = (date: Instant | ZonedDateTime | Date | string | number): Date => {
  if (date instanceof Date) return new Date(date);

  if (date instanceof Instant) {
    return convert(date).toDate();
  }

  if (date instanceof ZonedDateTime) {
    return convert(date).toDate();
  }

  if (typeof date === "number") {
    return new Date(date);
  }

  try {
    return new Date(Date.parse(date));
  } catch {
    throw new Error(`${T("error.invalid-instant-type")} ${date} (${typeof date})`);
  }
};

export const getStartOfYesterday = (zoneId: ZoneId): Date => {
  return ZonedDateTime.now(zoneId).truncatedTo(ChronoUnit.DAYS).minusDays(1).toDate();
};

export const getEndOfYesterday = (zoneId: ZoneId): Date => {
  return ZonedDateTime.now(zoneId).truncatedTo(ChronoUnit.DAYS).minusSeconds(1).toDate();
};
